import useReveal from '../hooks/useReveal';
import SectionHead from './SectionHead';

/**
 * Process — four real <ol> step cards, each rendered as an image.
 *
 * v2: the previous version used abstract CSS shape illustrations
 * (.ill-audit, .ill-design, .ill-build, .ill-launch). Those were
 * leftover from the SaaS palette and read as wireframes against the
 * new brand. Replaced with four custom-rendered step images that
 * live in /public/brand/process/ — each one a complete card showing
 * the step number, label, headline, body, and a polished device /
 * UI mockup illustrating that stage of the work.
 *
 * SEO + a11y notes:
 *   The text in each image is rendered as pixels, which Google can't
 *   index and screen readers can't announce. To preserve both:
 *     1. Every image has a descriptive `alt` covering the visible
 *        content (number, label, title, body) — this is the primary
 *        screen-reader path.
 *     2. The same text content is duplicated in a `.sr-only` block
 *        underneath so the page's reading order remains semantic
 *        (heading levels intact, body text in normal flow) and the
 *        content is double-anchored for crawlers.
 *
 * Performance: pictures ship WebP first (~95% smaller than PNG) with
 * PNG fallback. `srcSet` + `sizes` lets the browser pick 800/1200/1600
 * based on viewport width and DPI.
 */
interface Step {
  n: string;
  label: string;
  title: string;
  body: string;
  /** File-name slug, e.g. "step-1" → /brand/process/step-1-{800,1200,1600}.{png,webp} */
  image: string;
  /** Descriptive alt text covering the image's visible content. */
  alt: string;
}

const STEPS: Step[] = [
  {
    n: '01',
    label: 'Audit',
    title: 'We start by looking honestly.',
    body: 'A 30-minute call and a written critique of your current site — yours to keep, whether we work together or not.',
    image: 'step-1',
    alt: 'Step 01, Audit. We start by looking honestly. A 30-minute call and a written critique of your current site — yours to keep, whether we work together or not. The illustration shows a phone with an active call interface beside a Site Critique document with checked notes, all in brass-on-noir.',
  },
  {
    n: '02',
    label: 'Design',
    title: 'We design around your actual business.',
    body: 'Two weeks in, you see real design — not a mood board. Every decision is shaped by how your customers actually reach you.',
    image: 'step-2',
    alt: 'Step 02, Design. We design around your actual business. Two weeks in, you see real design — not a mood board. Every decision is shaped by how your customers actually reach you. The illustration shows a laptop and phone displaying a brass-accented website mockup with a navigation bar, headline, and design-stage progress tracker.',
  },
  {
    n: '03',
    label: 'Build',
    title: 'We build it properly.',
    body: 'Modern stack. No page builders. Hand-written code the next developer can read at a glance. Four to six weeks is typical.',
    image: 'step-3',
    alt: 'Step 03, Build. We build it properly. Modern stack. No page builders. Hand-written code the next developer can read at a glance. Four to six weeks is typical. The illustration shows a code editor with syntax highlighting and tech-stack badges for Next.js, TypeScript, Tailwind CSS, and Sanity CMS.',
  },
  {
    n: '04',
    label: 'Launch',
    title: 'We stay on after launch.',
    body: 'Thirty days of attention after go-live. The things you notice in real use are the things we fix before we sign off.',
    image: 'step-4',
    alt: 'Step 04, Launch. We stay on after launch. Thirty days of attention after go-live. The things you notice in real use are the things we fix before we sign off. The illustration shows a post-launch attention dashboard with a 30-day timeline, calendar view, and a fix-list checklist.',
  },
];

export default function Process() {
  return (
    <section id="process" className="section process">
      <div className="wrap">
        <SectionHead
          eyebrow="How it works"
          title={
            <>
              Four steps. <em className="serif">No surprises.</em>
            </>
          }
          body="A redesign shouldn't feel chaotic. From the first call to launch day, you always know where things stand and what's coming next."
        />

        <ol className="process-grid">
          {STEPS.map((s, i) => (
            <StepItem key={s.n} step={s} index={i} />
          ))}
        </ol>
      </div>
    </section>
  );
}

function StepItem({ step, index }: { step: Step; index: number }) {
  const ref = useReveal<HTMLLIElement>();
  const delay = index > 0 ? `d${Math.min(index, 4)}` : '';
  const base = `/brand/process/${step.image}`;
  return (
    <li ref={ref} className={`step reveal ${delay}`}>
      <picture>
        <source
          type="image/webp"
          srcSet={`${base}-800.webp 800w, ${base}-1200.webp 1200w, ${base}-1600.webp 1600w`}
          sizes="(max-width: 600px) 100vw, (max-width: 1240px) 50vw, 600px"
        />
        <img
          src={`${base}-1200.png`}
          srcSet={`${base}-800.png 800w, ${base}-1200.png 1200w, ${base}-1600.png 1600w`}
          sizes="(max-width: 600px) 100vw, (max-width: 1240px) 50vw, 600px"
          alt={step.alt}
          width={1536}
          height={1024}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      </picture>
      {/*
        Visually-hidden text mirror. The image already conveys the
        content visually, but Google can't index pixels and screen
        readers don't always announce alt text in normal reading order.
        This block keeps the page's heading hierarchy intact and the
        copy crawlable as plain text.
      */}
      <div className="sr-only">
        <p>
          {step.n} · {step.label}
        </p>
        <h3>{step.title}</h3>
        <p>{step.body}</p>
      </div>
    </li>
  );
}
